import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  BookOpen,
  FileText,
  ChevronRight,
  ArrowLeft,
  X,
  Copy,
  Check,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { searchCodes, browseCode } from "@bcc/shared-api";
import type { Article } from "@bcc/shared-types";
import {
  Input,
  Card,
  Spinner,
  ArticleCard,
  Button,
  Badge,
} from "@bcc/shared-ui";
import { useDebounce } from "@bcc/shared-ui";
import { SEO } from "../components/SEO";

// Clean and format article text for better readability
function formatArticleText(text: string): string {
  let cleaned = text;

  // Remove common PDF extraction artifacts
  cleaned = cleaned
    // Remove page markers like "--- Page 1 ---" or "Page 1 of 15"
    .replace(/---\s*Page\s*\d+\s*---/gi, "\n")
    .replace(/Page\s*\d+\s*of\s*\d+/gi, "")
    // Remove repeated copyright/footer lines
    .replace(/©\d{4}\s+Government of Alberta[^\n]*/gi, "")
    .replace(/Building STANDATA\s*\|\s*Alberta\.ca[^\n]*/gi, "")
    .replace(/Fire STANDATA\s*\|\s*Alberta\.ca[^\n]*/gi, "")
    .replace(/Municipal Affairs[^\n]*/gi, "")
    // Remove URL patterns that are just noise
    .replace(/https?:\/\/[^\s]+/g, "")
    // Clean up excessive whitespace
    .replace(/\n{4,}/g, "\n\n\n")
    .replace(/[ \t]+/g, " ")
    // Trim each line
    .split("\n")
    .map((line) => line.trim())
    .join("\n")
    // Remove leading/trailing whitespace
    .trim();

  return cleaned;
}

// Parse article text into structured sections
function parseArticleSections(text: string): Array<{
  type: "heading" | "subheading" | "paragraph" | "list";
  content: string;
}> {
  const sections: Array<{
    type: "heading" | "subheading" | "paragraph" | "list";
    content: string;
  }> = [];
  const lines = formatArticleText(text).split("\n");

  let currentParagraph: string[] = [];

  const flushParagraph = () => {
    if (currentParagraph.length > 0) {
      const content = currentParagraph.join(" ").trim();
      if (content) {
        sections.push({ type: "paragraph", content });
      }
      currentParagraph = [];
    }
  };

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) {
      flushParagraph();
      continue;
    }

    // Detect article/section numbers like "9.8.4.1." or "Article 1.3.3.3"
    if (
      /^(\d+\.)+\d*\.?\s+[A-Z]/.test(trimmed) ||
      /^Article\s+\d/i.test(trimmed)
    ) {
      flushParagraph();
      sections.push({ type: "heading", content: trimmed });
    }
    // Detect numbered list items like "(1)", "(2)", "(a)", "(b)"
    else if (/^\([0-9a-z]+\)/.test(trimmed)) {
      flushParagraph();
      sections.push({ type: "list", content: trimmed });
    }
    // Detect lettered list items like "a)", "b)"
    else if (/^[a-z]\)/.test(trimmed)) {
      flushParagraph();
      sections.push({ type: "list", content: trimmed });
    }
    // Detect Purpose, Scope, Application headers
    else if (
      /^(Purpose|Scope|Application|Background|Intent|Definition)s?:?\s*$/i.test(
        trimmed,
      )
    ) {
      flushParagraph();
      sections.push({ type: "subheading", content: trimmed });
    }
    // Regular paragraph content
    else {
      currentParagraph.push(trimmed);
    }
  }

  flushParagraph();
  return sections;
}

const codeCategories = [
  {
    id: "nbc",
    name: "National Building Code",
    description: "NBC 2020 with Alberta amendments",
    icon: "🏗️",
    color: "bg-blue-50 border-blue-200",
    exampleQueries: [
      "ceiling height",
      "stair width",
      "handrail",
      "bedroom window",
      "fire rating",
      "smoke alarm",
      "egress",
      "guard height",
    ],
  },
  {
    id: "npc",
    name: "National Plumbing Code",
    description: "NPC 2020",
    icon: "🚿",
    color: "bg-cyan-50 border-cyan-200",
    exampleQueries: [
      "fixture unit",
      "drain pipe",
      "venting",
      "water heater",
      "backflow",
      "trap",
    ],
  },
  {
    id: "nfc",
    name: "National Fire Code",
    description: "NFC 2020 with Alberta amendments",
    icon: "🔥",
    color: "bg-red-50 border-red-200",
    exampleQueries: [
      "smoke alarm",
      "fire extinguisher",
      "exit sign",
      "sprinkler",
      "fire separation",
      "emergency lighting",
    ],
  },
  {
    id: "necb",
    name: "National Energy Code",
    description: "NECB 2020",
    icon: "⚡",
    color: "bg-yellow-50 border-yellow-200",
    exampleQueries: [
      "insulation",
      "thermal resistance",
      "HVAC",
      "ventilation",
      "fenestration",
      "air barrier",
    ],
  },
  {
    id: "lub",
    name: "Land Use Bylaw",
    description: "Calgary Land Use Bylaw 1P2007",
    icon: "🏘️",
    color: "bg-green-50 border-green-200",
    exampleQueries: [
      "setback",
      "building height",
      "lot coverage",
      "parking",
      "R-C1",
      "R-C2",
    ],
  },
];

// Suggested search examples shown when no code is selected
const generalExamples = [
  { query: "ceiling height", code: "NBC", description: "Room dimensions" },
  { query: "fire rating", code: "NBC", description: "Fire separations" },
  { query: "smoke alarm", code: "NFC", description: "Fire safety" },
  { query: "handrail height", code: "NBC", description: "Stairs & guards" },
  { query: "egress window", code: "NBC", description: "Bedroom safety" },
  {
    query: "insulation R-value",
    code: "NECB",
    description: "Energy efficiency",
  },
];

export function ExplorePage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);
  const [copied, setCopied] = useState(false);
  const debouncedQuery = useDebounce(searchQuery, 300);

  const handleCopyArticle = () => {
    if (selectedArticle) {
      const text = `${selectedArticle.article_number} - ${selectedArticle.title}\n\n${selectedArticle.full_text}`;
      navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Search query - runs when user types a search term
  const { data: searchResults, isLoading: isSearching } = useQuery({
    queryKey: ["codeSearch", debouncedQuery, selectedCode],
    queryFn: () =>
      searchCodes(debouncedQuery, {
        code_name: selectedCode || undefined,
        limit: 20,
      }),
    enabled: debouncedQuery.length >= 2,
  });

  // Browse query - runs when a code category is selected without search
  const { data: browseResults, isLoading: isBrowsing } = useQuery({
    queryKey: ["codeBrowse", selectedCode],
    queryFn: () => browseCode(selectedCode!),
    enabled: !!selectedCode && debouncedQuery.length < 2,
  });

  const isLoading = isSearching || isBrowsing;
  const selectedCategory = codeCategories.find((c) => c.id === selectedCode);

  return (
    <>
      <SEO />
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BookOpen className="h-6 w-6 text-teal-500" />
            Explore Building Codes
          </h1>
          <p className="text-gray-500 mt-1">
            Search and browse all building codes including NBC, NPC, NFC, NECB,
            and Land Use Bylaw.
          </p>
        </div>

        {/* Search */}
        <Card variant="outlined" padding="md">
          <Input
            placeholder="Search articles, requirements, definitions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            leftIcon={<Search className="h-5 w-5" />}
            rightIcon={isLoading ? <Spinner size="sm" /> : undefined}
          />

          {/* Code filters */}
          <div className="flex flex-wrap gap-2 mt-3">
            <button
              onClick={() => setSelectedCode(null)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                selectedCode === null
                  ? "bg-teal-100 text-teal-700"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              All Codes
            </button>
            {codeCategories.map((code) => (
              <button
                key={code.id}
                onClick={() =>
                  setSelectedCode(code.id === selectedCode ? null : code.id)
                }
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  selectedCode === code.id
                    ? "bg-teal-100 text-teal-700"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {code.icon} {code.name}
              </button>
            ))}
          </div>

          {/* Suggested searches - show when not searching */}
          {searchQuery.length < 2 && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-xs text-gray-500 mb-2">
                {selectedCategory ? `Try searching for:` : `Popular searches:`}
              </p>
              <div className="flex flex-wrap gap-2">
                {selectedCategory
                  ? selectedCategory.exampleQueries.map((query) => (
                      <button
                        key={query}
                        onClick={() => setSearchQuery(query)}
                        className="px-2.5 py-1 text-xs bg-gray-50 hover:bg-teal-50 text-gray-600 hover:text-teal-700 rounded-md border border-gray-200 hover:border-teal-200 transition-colors"
                      >
                        {query}
                      </button>
                    ))
                  : generalExamples.map((example) => (
                      <button
                        key={example.query}
                        onClick={() => setSearchQuery(example.query)}
                        className="px-2.5 py-1 text-xs bg-gray-50 hover:bg-teal-50 text-gray-600 hover:text-teal-700 rounded-md border border-gray-200 hover:border-teal-200 transition-colors group"
                      >
                        <span>{example.query}</span>
                        <span className="ml-1.5 text-gray-400 group-hover:text-teal-500">
                          · {example.code}
                        </span>
                      </button>
                    ))}
              </div>
            </div>
          )}
        </Card>

        {/* Results or Categories */}
        {debouncedQuery.length >= 2 ? (
          // Search results
          <div className="space-y-3">
            <p className="text-sm text-gray-500">
              {searchResults?.total_count || 0} results for "{debouncedQuery}"
            </p>
            {searchResults?.articles.map((article) => (
              <ArticleCard
                key={article.id}
                article={article}
                onClick={() => setSelectedArticle(article)}
              />
            ))}
            {searchResults?.articles.length === 0 && !isLoading && (
              <Card variant="outlined" className="text-center py-12">
                <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">
                  No articles found matching your search.
                </p>
                <p className="text-sm text-gray-400 mt-1">
                  Try different keywords or browse by category.
                </p>
              </Card>
            )}
          </div>
        ) : selectedCode && selectedCategory ? (
          // Browse results for selected code
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSelectedCode(null)}
                className="flex items-center gap-1 text-sm text-teal-600 hover:text-teal-700"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to all codes
              </button>
            </div>
            <div className="flex items-center gap-2 mb-4">
              <span className="text-2xl">{selectedCategory.icon}</span>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {selectedCategory.name}
                </h2>
                <p className="text-sm text-gray-500">
                  {browseResults?.total || 0} articles
                </p>
              </div>
            </div>
            {isBrowsing ? (
              <div className="flex justify-center py-12">
                <Spinner size="lg" />
              </div>
            ) : (
              <>
                {browseResults?.items.map((article) => (
                  <ArticleCard
                    key={article.id}
                    article={article}
                    onClick={() => setSelectedArticle(article)}
                  />
                ))}
                {browseResults?.items.length === 0 && (
                  <Card variant="outlined" className="text-center py-12">
                    <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">
                      No articles found in this code.
                    </p>
                  </Card>
                )}
              </>
            )}
          </div>
        ) : (
          // Code category cards
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {codeCategories.map((code, index) => (
              <motion.div
                key={code.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card
                  variant="outlined"
                  className={`cursor-pointer hover:shadow-md transition-shadow ${code.color}`}
                  onClick={() => setSelectedCode(code.id)}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <span className="text-3xl">{code.icon}</span>
                      <h3 className="font-semibold text-gray-900 mt-2">
                        {code.name}
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">
                        {code.description}
                      </p>
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        )}

        {/* Article Detail Modal */}
        <AnimatePresence>
          {selectedArticle && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
              onClick={() => setSelectedArticle(null)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Modal Header */}
                <div className="flex items-start justify-between p-6 border-b border-gray-100">
                  <div className="flex-1 min-w-0 pr-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-lg font-mono text-teal-600 font-semibold">
                        {selectedArticle.article_number}
                      </span>
                      <Badge variant="default" size="sm">
                        {selectedArticle.code_name}
                      </Badge>
                    </div>
                    <h2 className="text-xl font-bold text-gray-900">
                      {selectedArticle.title || "Untitled Article"}
                    </h2>
                  </div>
                  <button
                    onClick={() => setSelectedArticle(null)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="h-5 w-5 text-gray-500" />
                  </button>
                </div>

                {/* Modal Body */}
                <div className="flex-1 overflow-y-auto p-6">
                  <div className="space-y-3">
                    {parseArticleSections(selectedArticle.full_text).map(
                      (section, index) => {
                        switch (section.type) {
                          case "heading":
                            return (
                              <h3
                                key={index}
                                className="text-base font-semibold text-gray-900 mt-4 first:mt-0 border-b border-gray-100 pb-2"
                              >
                                {section.content}
                              </h3>
                            );
                          case "subheading":
                            return (
                              <h4
                                key={index}
                                className="text-sm font-semibold text-teal-700 mt-4 uppercase tracking-wide"
                              >
                                {section.content}
                              </h4>
                            );
                          case "list":
                            return (
                              <div
                                key={index}
                                className="flex gap-2 text-sm text-gray-700 pl-4 py-1 border-l-2 border-gray-200 bg-gray-50 rounded-r"
                              >
                                <span className="font-mono text-teal-600 flex-shrink-0">
                                  {
                                    section.content.match(
                                      /^\([0-9a-z]+\)|^[a-z]\)/,
                                    )?.[0]
                                  }
                                </span>
                                <span>
                                  {section.content
                                    .replace(
                                      /^\([0-9a-z]+\)\s*|^[a-z]\)\s*/,
                                      "",
                                    )
                                    .trim()}
                                </span>
                              </div>
                            );
                          case "paragraph":
                          default:
                            return (
                              <p
                                key={index}
                                className="text-sm text-gray-700 leading-relaxed"
                              >
                                {section.content}
                              </p>
                            );
                        }
                      },
                    )}
                    {parseArticleSections(selectedArticle.full_text).length ===
                      0 && (
                      <p className="text-sm text-gray-500 italic">
                        No content available for this article.
                      </p>
                    )}
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="flex items-center justify-between p-4 border-t border-gray-100 bg-gray-50">
                  <div className="text-sm text-gray-500">
                    {selectedArticle.part && (
                      <span>Part {selectedArticle.part}</span>
                    )}
                    {selectedArticle.section && (
                      <span> · Section {selectedArticle.section}</span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCopyArticle}
                    >
                      {copied ? (
                        <>
                          <Check className="h-4 w-4 mr-1" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4 mr-1" />
                          Copy Text
                        </>
                      )}
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => setSelectedArticle(null)}
                      className="bg-teal-600 hover:bg-teal-700"
                    >
                      Close
                    </Button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
}
